import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
	updateFreelancerOnboardingExpeditedStatus,
	updateFreelancerOnboardingFormsProgressCurrentStep as updateFreelancerOnboardingFormsProgressCurrentStepAsync,
	updateFreelancerOnboardingStatus,
	submitFreelancerOnboardingExperienceRating,
	GetFreelancerOnboardingStatusTracks,
	GetFreelancerSoftSkillsEvaluations
} from "../api/freelancer-onboardings-api";
import { useLinearProgressContext } from "../contexts/useLinearProgressContext";
import { useSnackbarContext } from "../contexts/useSnackBarContext";
import { FreelancerOnboardingFormStep } from "../models/enums/FreelancerOnboardingFormStep";
import { FreelancerOnboardingStatus } from "../models/enums/FreelancerOnboardingStatus";
import { SaveOnboardingOptions } from "./useFreelancerProfile";
import { FreelancerOnboardingStatusTrack } from "../models/FreelancerOnboardingStatusTrack";
import { FreelancerOnboardingSoftSkillsEvaluation } from "../models/FreelancerOnboardingSoftSkillsEvaluation";

/**
 * Hook to update freelancer onboarding status
 */
export const useUpdateFreelancerOnboardingStatus = () => {
	const { showSuccess, showError, showSnackbar } = useSnackbarContext();
	const { showProgress, hideProgress } = useLinearProgressContext();
	const { t } = useTranslation();

	return useMutation({
		mutationFn: async ({
			freelancerOnboardingId,
			newStatus
		}: {
			freelancerOnboardingId: number;
			newStatus: FreelancerOnboardingStatus;
		}) => {
			showProgress();
			await updateFreelancerOnboardingStatus(freelancerOnboardingId, newStatus);
		},
		onSuccess: (_, variables) => {
			hideProgress();

			// Show special message for Soft Skills Evaluation status with longer duration
			if (variables.newStatus === FreelancerOnboardingStatus.SoftSkillsEvaluation) {
				showSnackbar({
					message: t("freelancerOnboarding.softSkillsEvaluationStatusUpdated"),
					severity: "success",
					duration: 20000 // 20 seconds duration
				});
			} else {
				showSuccess(t("freelancerOnboarding.statusUpdated"));
			}
		},
		onError: error => {
			hideProgress();
			console.error("Error updating freelancer onboarding status:", error);
			showError(t("freelancerOnboarding.statusUpdateError"));
		}
	});
};

/**
 * Hook to update freelancer onboarding forms progress current step
 */
export const useUpdateFreelancerOnboardingFormsProgressCurrentStep = ({
	hideAllSnackbars = false
}: SaveOnboardingOptions = {}) => {
	const { showSuccess, showError } = useSnackbarContext();
	const { showProgress, hideProgress } = useLinearProgressContext();
	const { t } = useTranslation();

	return useMutation({
		mutationFn: async ({
			freelancerOnboardingId,
			freelancerOnboardingFormsProgressId,
			newStep
		}: {
			freelancerOnboardingId: number;
			freelancerOnboardingFormsProgressId: number;
			newStep: FreelancerOnboardingFormStep;
		}) => {
			showProgress();
			await updateFreelancerOnboardingFormsProgressCurrentStepAsync(
				freelancerOnboardingId,
				freelancerOnboardingFormsProgressId,
				newStep
			);
		},
		onSuccess: (_, __) => {
			hideProgress();

			!hideAllSnackbars && showSuccess(t("freelancerOnboardingForms.stepUpdated"));
		},
		onError: error => {
			hideProgress();
			console.error(
				"Error updating freelancer onboarding forms progress current step:",
				error
			);
			!hideAllSnackbars && showError(t("freelancerOnboardingForms.stepUpdateError"));
		}
	});
};

/**
 * Hook to update freelancer onboarding expedited status
 */
export const useUpdateFreelancerOnboardingExpeditedStatus = () => {
	const { showSuccess, showError } = useSnackbarContext();
	const { showProgress, hideProgress } = useLinearProgressContext();
	const { t } = useTranslation();

	return useMutation({
		mutationFn: async ({
			freelancerOnboardingId,
			newIsOnboardingCurrentlyExpedited
		}: {
			freelancerOnboardingId: number;
			newIsOnboardingCurrentlyExpedited: boolean;
		}) => {
			showProgress();
			await updateFreelancerOnboardingExpeditedStatus(
				freelancerOnboardingId,
				newIsOnboardingCurrentlyExpedited
			);
		},
		onSuccess: (_, __) => {
			hideProgress();

			showSuccess(t("freelancerOnboarding.expeditedStatusUpdated"));
		},
		onError: error => {
			hideProgress();
			console.error("Error updating freelancer onboarding expedited status:", error);
			showError(t("freelancerOnboarding.expeditedStatusUpdateError"));
		}
	});
};

/**
 * Hook to submit freelancer onboarding experience rating
 */
export const useSubmitFreelancerOnboardingExperienceRating = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useSnackbarContext();
	const { showProgress, hideProgress } = useLinearProgressContext();
	const { t } = useTranslation();

	return useMutation({
		mutationFn: async ({
			freelancerOnboardingId,
			rating,
			feedback
		}: {
			freelancerOnboardingId: number;
			rating: number;
			feedback?: string;
		}) => {
			showProgress();
			await submitFreelancerOnboardingExperienceRating(
				freelancerOnboardingId,
				rating,
				feedback
			);
		},
		onSuccess: () => {
			hideProgress();
			showSuccess(t("freelancerOnboarding.experienceFeedbackSubmitted"));

			queryClient.invalidateQueries({
				queryKey: ["freelancerProfile"]
			});
		},
		onError: error => {
			hideProgress();
			console.error("Error submitting freelancer onboarding experience rating:", error);
			showError(t("freelancerOnboarding.experienceFeedbackSubmitError"));
		}
	});
};

export const useFreelancerOnboardingStatusTracks = (freelancerOnboardingId: number) => {
	return useQuery<FreelancerOnboardingStatusTrack[]>({
		queryKey: ["freelancerOnboardingStatusTracks", freelancerOnboardingId],
		queryFn: () => GetFreelancerOnboardingStatusTracks(freelancerOnboardingId),
		enabled: freelancerOnboardingId > 0
	});
};

export const useFreelancerSoftSkillsEvaluations = (freelancerOnboardingId: number) => {
	return useQuery<FreelancerOnboardingSoftSkillsEvaluation[]>({
		queryKey: ["freelancerSoftSkillsEvaluations", freelancerOnboardingId],
		queryFn: () => GetFreelancerSoftSkillsEvaluations(freelancerOnboardingId),
		enabled: freelancerOnboardingId > 0
	});
};
